import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { AlertTriangle, Wand2 } from 'lucide-react';
import { useSipPeers } from '../../../../hooks/useSipPeers';
import { usePeerGroups } from '../../../../hooks/usePeerGroups';
import { useSipManipulationsForDropdown } from '../../../../hooks/useSipManipulations';
import { FormField, FormSelect, SectionCard, StepHeader, InlineLoadingBox } from '../../../common';
import type { RoutingRuleFormData } from '../schema';

/** Destination tab: route target + SIP manipulation profiles. */
export function DestinationTab({
  form,
  unsupportedDestinationType = null,
}: {
  form: UseFormReturn<RoutingRuleFormData>;
    /**
     * When set, the rule uses a destination type the console cannot edit (e.g.
     * `gateway`, `pstn`, `forward`). The destination section becomes read-only
     * with a warning banner.
     */
  unsupportedDestinationType?: string | null;
}) {
  const destinationType = form.watch('destinationType');
  const destinationTarget = form.watch('destinationTarget');

  const { data: peersResponse, isLoading: peersLoading } = useSipPeers();
  const { data: groupsResponse, isLoading: groupsLoading } = usePeerGroups();
  const { data: manipulations, isLoading: manipulationsLoading } = useSipManipulationsForDropdown();

  const peers = peersResponse?.data || [];
  const peerGroups = groupsResponse?.data || [];

  const handleDestinationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    form.setValue('destinationType', e.target.value as 'peer' | 'peerGroup' | 'reject');
    form.setValue('destinationTarget', '');
  };

  return (
    <div className="space-y-6">
      <StepHeader
        title="Routing Actions"
        description="Define the destination and route-level manipulation overrides that run after peer-level policy."
      />

      <div className="space-y-6">
        {/* Route Target */}
        {unsupportedDestinationType ? (
          <SectionCard title="Route Target">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                <div className="space-y-1">
                  <p className="font-medium text-amber-900">
                    This rule uses destination type &lsquo;{unsupportedDestinationType}&rsquo;
                    which is not editable in the console.
                  </p>
                  <p>
                    This destination type is not editable in the console.
                  </p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-amber-200 pt-3">
                <div>
                  <span className="text-xs font-medium uppercase tracking-wide text-amber-900">
                    Destination type
                  </span>
                  <div className="mt-0.5 font-medium text-amber-900">
                    {unsupportedDestinationType}
                  </div>
                </div>
                <div>
                  <span className="text-xs font-medium uppercase tracking-wide text-amber-900">
                    Target
                  </span>
                  <div className="mt-0.5 break-all font-medium text-amber-900">
                    {destinationTarget || '-'}
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard title="Route Target">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Destination Type">
                <FormSelect
                  value={destinationType}
                  onChange={handleDestinationTypeChange}
                >
                  <option value="peer">SIP Peer</option>
                  <option value="peerGroup">Peer Group</option>
                  <option value="reject">Reject Call</option>
                </FormSelect>
              </FormField>

              {/* Conditional Target Selection */}
              {destinationType === 'peer' && (
                <FormField
                  label="Select SIP Peer"
                  required
                  error={form.formState.errors.destinationTarget?.message}
                >
                  {peersLoading ? (
                    <InlineLoadingBox message="Loading peers..." />
                  ) : (
                    <FormSelect {...form.register('destinationTarget')}>
                      <option value="">Select a SIP peer...</option>
                      {peers.map((peer) => (
                        <option key={peer.id} value={peer.id}>
                          {peer.name} ({peer.type})
                        </option>
                      ))}
                    </FormSelect>
                  )}
                  {peers.length === 0 && !peersLoading && (
                    <p className="mt-1 text-sm text-amber-600">No SIP peers available. Create a peer first.</p>
                  )}
                </FormField>
              )}

              {destinationType === 'peerGroup' && (
                <FormField
                  label="Select Peer Group"
                  required
                  error={form.formState.errors.destinationTarget?.message}
                >
                  {groupsLoading ? (
                    <InlineLoadingBox message="Loading groups..." />
                  ) : (
                    <FormSelect {...form.register('destinationTarget')}>
                      <option value="">Select a peer group...</option>
                      {peerGroups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.peerCount} peers)
                        </option>
                      ))}
                    </FormSelect>
                  )}
                  {peerGroups.length === 0 && !groupsLoading && (
                    <p className="mt-1 text-sm text-amber-600">No peer groups available. Create a group first.</p>
                  )}
                </FormField>
              )}

              {destinationType === 'reject' && (
                <FormField label="Rejection Reason" hint="SIP response code to send">
                  <FormSelect {...form.register('destinationTarget')}>
                    <option value="403">403 Forbidden</option>
                    <option value="404">404 Not Found</option>
                    <option value="486">486 Busy Here</option>
                    <option value="503">503 Service Unavailable</option>
                  </FormSelect>
                </FormField>
              )}
            </div>
          </SectionCard>
        )}

        {/* SIP Manipulations */}
        <SectionCard
          title="Route-level manipulations (Optional)"
          description="Route overrides run after peer-level manipulation. Configure reusable profiles in Connectivity > SIP Manipulations."
          icon={Wand2}
          variant="blue"
          bordered
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Route inbound manipulation"
              hint="Applied after peer inbound manipulation when this rule matches"
            >
              {manipulationsLoading ? (
                <InlineLoadingBox message="Loading profiles..." />
              ) : (
                <FormSelect {...form.register('inboundManipulationId')}>
                  <option value="">None</option>
                  {manipulations?.data?.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name} ({profile.ruleCount} rules)
                    </option>
                  ))}
                </FormSelect>
              )}
            </FormField>

            <FormField
              label="Route outbound manipulation"
              hint="Applied after peer outbound manipulation when forwarding matched calls"
            >
              {manipulationsLoading ? (
                <InlineLoadingBox message="Loading profiles..." />
              ) : (
                <FormSelect {...form.register('outboundManipulationId')}>
                  <option value="">None</option>
                  {manipulations?.data?.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name} ({profile.ruleCount} rules)
                    </option>
                  ))}
                </FormSelect>
              )}
            </FormField>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
